import { Nullable, RequestState } from 'modules/types/common';
import { combineReducers, createReducer } from '@reduxjs/toolkit';
import { switchAccountSuccess } from 'store/account/actions';
import * as actions from 'store/vitals/actions';
import { setFetchingState, setFetchingFailState } from 'store/setLocalStore';

import { normalizedHistoricalVitals } from './mappers';
import { ShareVitals, Vital } from './types';

type VitalsDownloadStatus = 'pending' | 'success' | 'fail';

export interface VitalsState {
  recentVitals: VitalsListState;
  activeVisitVitals: VisitSummaryVitals;
  historicalVitals: HistoricalVitalsListState;
  downloadVitals: DownLoadVitalsState;
  downloadVisitVitals: DownLoadVisitVitalsState;
  shareVitals: ShareVitalsState;
}

export interface VisitSummaryVitals {
  isFetching: boolean;
  data: Vital[];
  error: Nullable<Error>;
}
export interface VitalsListState extends VisitSummaryVitals {
  lastUpdated: string | undefined;
  requestState: RequestState;
}

export const visitSummaryVitalsInitialState = (): VisitSummaryVitals => ({
  isFetching: false,
  data: [],
  error: null
});

export const vitalsListInitialState = (): VitalsListState => ({
  isFetching: false,
  data: [],
  error: null,
  lastUpdated: undefined,
  requestState: RequestState.INITIALIZED
});

/**
 *  Recent Vitals
 */

export const recentVitalsListReducer = createReducer(vitalsListInitialState(), {
  [switchAccountSuccess.type]: () => vitalsListInitialState(),
  [actions.GET_VITALS_DATA]: (state, action) => {
    setFetchingState(state, action);
    state.requestState = RequestState.REQUESTING;
  },
  [actions.GET_VITALS_DATA_FAIL]: (state, action) => {
    setFetchingFailState(state, action);
    state.requestState = RequestState.ERROR;
  },
  [actions.GET_VITALS_DATA_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.data = action.payload.data;
    state.requestState = RequestState.SUCCESS;
  },
  [actions.GET_VITALS_CACHE_SUCCESS]: (state, action) => {
    state.data = action.payload.data;
    state.lastUpdated = action.payload.cacheUpdatedAt;
  }
});

/**
 *  Active Visit Vitals
 */

export const activeVisitVitalsListReducer = createReducer(visitSummaryVitalsInitialState(), {
  [switchAccountSuccess.type]: () => visitSummaryVitalsInitialState(),
  [actions.GET_VITALS_BY_ENCOUNTER_ID]: state => {
    state.isFetching = true;
    state.data = [];
    state.error = null;
  },
  [actions.GET_VITALS_BY_ENCOUNTER_ID_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
  },
  [actions.GET_VITALS_BY_ENCOUNTER_ID_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.data = action.payload.data;
  }
});

/**
 *  Historical Vital
 */
export interface HistoricalVitalsListState {
  isFetching: boolean;
  data: Vital[];
  error: Nullable<Error>;
  currentTypeFetch: string;
}

export const historicalInitialState = (): HistoricalVitalsListState => ({
  isFetching: false,
  data: [],
  error: null,
  currentTypeFetch: ''
});

const historicalVitalListReducer = createReducer(historicalInitialState(), {
  [switchAccountSuccess.type]: () => historicalInitialState(),
  [actions.GET_VITALS_BY_TYPE]: (state, action) => {
    setFetchingState(
      state,
      action,
      () => state.currentTypeFetch === action.payload.request.params.vitalName
    );

    state.currentTypeFetch = action.payload.request.params.vitalName;
  },
  [actions.GET_VITALS_BY_TYPE_FAIL]: setFetchingFailState,
  [actions.GET_VITALS_BY_TYPE_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.data = normalizedHistoricalVitals(action.payload.data);
  },
  [actions.SET_HISTORICAL_TYPE]: (state, action) => {
    state.currentTypeFetch = action.payload.type;
  }
});

/**
 *  Recent Vitals Download
 */

export interface DownLoadVitalsState {
  isDownloadingVitals: boolean;
  error: Nullable<Error>;
}

export const downLoadVitalsInitialState = (): DownLoadVitalsState => ({
  isDownloadingVitals: false,
  error: null
});

const downloadRecentVitalsReducer = createReducer(downLoadVitalsInitialState(), {
  [actions.DOWNLOAD_VITALS]: state => {
    state.isDownloadingVitals = true;
    state.error = null;
  },
  [actions.DOWNLOAD_VITALS_FAIL]: (state, action) => {
    state.isDownloadingVitals = false;
    state.error = action.error;
  },
  [actions.DOWNLOAD_VITALS_SUCCESS]: state => {
    state.isDownloadingVitals = false;
  }
});

/**
 *  Active Visit Vitals Download
 */

export interface DownLoadVisitVitalsState {
  vitalsDownloadsByEncounter: Record<string, VitalsDownloadStatus>;
  error: Nullable<Error>;
}

export const downLoadVisitVitalsInitialState = (): DownLoadVisitVitalsState => ({
  vitalsDownloadsByEncounter: {},
  error: null
});

const downloadActiveVisitVitalsReducer = createReducer(downLoadVisitVitalsInitialState(), {
  [actions.DOWNLOAD_VITALS_BY_ENCOUNTER]: (state, action) => {
    state.vitalsDownloadsByEncounter = { [action.payload.id]: 'pending' };
    state.error = null;
  },
  [actions.DOWNLOAD_VITALS_BY_ENCOUNTER_FAIL]: (state, action) => {
    state.vitalsDownloadsByEncounter = { [action.payload.id]: 'fail' };
    state.error = action.error;
  },
  [actions.DOWNLOAD_VITALS_BY_ENCOUNTER_SUCCESS]: (state, action) => {
    state.vitalsDownloadsByEncounter = { [action.payload.id]: 'success' };
  }
});

/**
 *  Share Vitals
 */

export interface ShareVitalsState {
  isSendingVitals: boolean;
  sharedVitalsData: ShareVitals | {};
  sharedVitalsError: Nullable<Error>;
}

export const shareVitalsInitialState = (): ShareVitalsState => ({
  isSendingVitals: false,
  sharedVitalsData: {},
  sharedVitalsError: null
});

const shareRecentVitalsReducer = createReducer(shareVitalsInitialState(), {
  [actions.SHARE_VITALS]: state => {
    state.isSendingVitals = true;
    state.sharedVitalsError = null;
  },
  [actions.SHARE_VITALS_FAIL]: (state, action) => {
    state.isSendingVitals = false;
    state.sharedVitalsError = action.error;
  },
  [actions.SHARE_VITALS_SUCCESS]: (state, action) => {
    state.isSendingVitals = false;
    state.sharedVitalsData = action.payload.data;
  }
});

export default combineReducers({
  recentVitals: recentVitalsListReducer,
  activeVisitVitals: activeVisitVitalsListReducer,
  historicalVitals: historicalVitalListReducer,
  downloadVitals: downloadRecentVitalsReducer,
  downloadVisitVitals: downloadActiveVisitVitalsReducer,
  shareVitals: shareRecentVitalsReducer
});
